import React from "react";
import { Loader2, KeyRound, AlertCircle, CheckCircle2 } from "lucide-react";
import PasswordWithMeter from "./PasswordWithMeter";

/**
 * Iter269.15 — High-level password-change card.
 *
 * Encapsulates title + description + optional CURRENT PASSWORD field +
 * <PasswordWithMeter /> + error/success banners + busy-aware submit
 * button into a single drop-in component. Every future "set a new
 * password" / "change your password" / "accept invite" screen drops in
 * with ~10 lines of JSX instead of ~60.
 *
 * Parent contract:
 *   ```jsx
 *   <PasswordChangeCard
 *     title="Change your password"
 *     description="Pick a strong password (9+ chars, …)"
 *     onSubmit={async (password, currentPassword) => {
 *       await api.changePassword({ current_password: currentPassword, password });
 *     }}
 *     requireCurrentPassword
 *     submitLabel="Update password"
 *     successMessage="Password updated."
 *     testIdPrefix="settings-pw"
 *   />
 *   ```
 *
 *   • `onSubmit(password, currentPassword?)` is awaited. Throw to surface
 *     an error banner. Return value is ignored (the card manages its own
 *     success / busy state). `currentPassword` is only passed when
 *     `requireCurrentPassword` is true.
 *   • `onSuccess()` fires after the submit promise resolves AND the
 *     success banner has been shown. Use this to navigate away.
 *   • `header` / `footer` are React-node slots for parent-specific chrome
 *     (e.g. AcceptInvite's invite-summary block, PasswordReset's "← Back
 *     to sign in" link). Both are optional.
 */
export default function PasswordChangeCard({
  // Copy
  title,
  description,
  submitLabel = "Save password",
  successMessage = "Password updated.",
  successDuration = 2800,
  // Flow
  onSubmit,
  onSuccess,
  // Layout slots
  header,
  footer,
  // Behaviour
  requireCurrentPassword = false,
  currentPasswordLabel = "CURRENT PASSWORD",
  // PasswordWithMeter passthroughs
  newPasswordLabel = "NEW PASSWORD",
  confirmLabel = "CONFIRM",
  blockOnBreach = true,
  breachCheckEnabled = true,
  showSuggester = true,
  // Test scaffolding
  testIdPrefix = "pw-card",
  // Style overrides for the parent — most surfaces won't need these.
  className = "",
}) {
  const [cur, setCur] = React.useState("");
  const [validPassword, setValidPassword] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [ok, setOk]   = React.useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr("");
    setOk(false);
    if (!validPassword) {
      setErr("Password doesn't meet the policy below.");
      return;
    }
    if (requireCurrentPassword && !cur) {
      setErr("Enter your current password.");
      return;
    }
    setBusy(true);
    try {
      await onSubmit(
        validPassword,
        requireCurrentPassword ? cur : undefined,
      );
      setOk(true);
      setCur("");
      // Tell PasswordWithMeter to clear by remounting via key bump;
      // the wrapper owns the input state and clearing it from outside
      // would mean a controlled-input refactor. The remount path is
      // simple and keeps the wrapper black-box.
      _bumpKey();
      const t = setTimeout(() => {
        setOk(false);
        onSuccess?.();
      }, successDuration);
      // Track so we can cancel on unmount.
      cleanupRef.current.push(() => clearTimeout(t));
    } catch (e2) {
      setErr(
        e2?.response?.data?.detail
          || e2?.message
          || "Could not save password.",
      );
    } finally {
      setBusy(false);
    }
  };

  // Remount PasswordWithMeter to reset its internal inputs after a
  // successful save. Simpler + safer than threading a `reset()` ref
  // through the wrapper just for this case.
  const [meterKey, setMeterKey] = React.useState(0);
  const _bumpKey = () => setMeterKey((k) => k + 1);
  // Wipe out any pending timeouts on unmount to avoid setState-after-
  // unmount warnings during quick navigations.
  const cleanupRef = React.useRef([]);
  React.useEffect(() => () => {
    cleanupRef.current.forEach((fn) => fn());
    cleanupRef.current = [];
  }, []);

  return (
    <form
      onSubmit={submit}
      className={className}
      data-testid={`${testIdPrefix}-form`}
    >
      {title && (
        <h2
          className="font-bold"
          data-testid={`${testIdPrefix}-title`}
          style={{ color: "#fff", fontSize: "1.35rem", margin: 0 }}
        >
          {title}
        </h2>
      )}
      {description && (
        <div
          className="text-sm"
          data-testid={`${testIdPrefix}-description`}
          style={{ color: "var(--qt-text-2)", lineHeight: 1.55, marginTop: 6 }}
        >
          {description}
        </div>
      )}

      {header && <div className="mt-3" data-testid={`${testIdPrefix}-header`}>{header}</div>}

      <div className="mt-4 grid grid-cols-1 gap-3">
        {requireCurrentPassword && (
          <div>
            <div className="qt-label mb-2" id={`${testIdPrefix}-cur-label`}>
              {currentPasswordLabel}
            </div>
            <input
              type="password"
              className="qt-input"
              required
              autoComplete="current-password"
              aria-labelledby={`${testIdPrefix}-cur-label`}
              data-testid={`${testIdPrefix}-current`}
              value={cur}
              onChange={(e) => setCur(e.target.value)}
            />
          </div>
        )}

        <PasswordWithMeter
          key={meterKey}
          onValid={setValidPassword}
          newPasswordLabel={newPasswordLabel}
          confirmLabel={confirmLabel}
          blockOnBreach={blockOnBreach}
          breachCheckEnabled={breachCheckEnabled}
          showSuggester={showSuggester}
          testIdPrefix={testIdPrefix}
        />

        {err && (
          <div
            className="text-xs px-3 py-2 rounded inline-flex items-start gap-2"
            data-testid={`${testIdPrefix}-error`}
            style={{
              background: "rgba(255,42,42,0.08)",
              border: "1px solid rgba(255,42,42,0.4)",
              color: "#FF6E6E",
              lineHeight: 1.5,
            }}
          >
            <AlertCircle size={13} className="mt-[1px] flex-shrink-0" />
            <span>{err}</span>
          </div>
        )}

        {ok && (
          <div
            className="text-xs px-3 py-2 rounded inline-flex items-start gap-2"
            data-testid={`${testIdPrefix}-saved`}
            style={{
              background: "rgba(31,221,131,0.08)",
              border: "1px solid rgba(31,221,131,0.30)",
              color: "#1FDD83",
              lineHeight: 1.5,
            }}
          >
            <CheckCircle2 size={13} className="mt-[1px] flex-shrink-0" />
            <span>{successMessage}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={busy || !validPassword || (requireCurrentPassword && !cur)}
          data-testid={`${testIdPrefix}-submit`}
          className="qt-btn qt-btn-primary mt-2 inline-flex items-center justify-center gap-2"
          style={{ fontSize: 13 }}
        >
          {busy ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Saving…
            </>
          ) : (
            <>
              <KeyRound size={13} /> {submitLabel}
            </>
          )}
        </button>
      </div>

      {footer && (
        <div className="mt-3" data-testid={`${testIdPrefix}-footer`}>
          {footer}
        </div>
      )}
    </form>
  );
}
